import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, useWishlist } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';
import { resilientFetch } from '../lib/resilientFetch';
import MoleculeLoader from '../components/MoleculeLoader';
import { 
  Search, 
  Filter,
  Activity,
  Flame,
  Brain,
  Dna,
  FlaskConical,
  X,
  PackageCheck,
  Heart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format image URLs (handle local uploads vs external URLs)
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/uploads') || url.startsWith('/api/uploads')) return `${API_URL}${url}`;
  return url;
};

// Thumbnail URL for product grid (compressed WebP)
const getThumbnailUrl = (productId) => {
  return `${API_URL}/api/product-thumbnails/${productId}?w=400&q=70`;
};

const categoryIcons = {
  'recovery-repair': Activity,
  'metabolic': Flame,
  'cognitive': Brain,
  'longevity': Dna,
};

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  
  const activeCategory = searchParams.get('category') || '';

  const loadProducts = React.useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const url = `${API_URL}/api/products${activeCategory ? `?category=${activeCategory}` : ''}`;
    const [productsRes, categoriesRes] = await Promise.all([
      resilientFetch(url, { headers }),
      resilientFetch(`${API_URL}/api/categories`),
    ]);
    if (productsRes.ok) {
      setProducts(productsRes.data || []);
    } else {
      // Don't blow away previously-good data on a transient retry-failure;
      // surface a distinct error state so the user can hit "Retry" instead of
      // the misleading "No products found" empty state.
      setFetchError(productsRes.error?.message || 'Could not load products');
    }
    if (categoriesRes.ok) setCategories(categoriesRes.data || []);
    setLoading(false);
  }, [activeCategory, token]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // ---- Auto-recovery when the catalog fetch fails ----
  // Customers reported having to manually click "Try Again" or refresh after
  // a transient blip (deploy cold-start, brief network drop). We now:
  //   1. Silently retry every 4s while in the fetchError state, capped at
  //      6 attempts so we don't hammer a truly-down backend forever.
  //   2. Re-fetch immediately when the browser fires `online` (wifi back).
  // Both cancel as soon as products load successfully.
  useEffect(() => {
    if (!fetchError) return;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (attempts > 6) {
        clearInterval(interval);
        return;
      }
      loadProducts();
    }, 4000);
    const onOnline = () => loadProducts();
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(interval);
      window.removeEventListener('online', onOnline);
    };
  }, [fetchError, loadProducts]);

  // Check if product has any variant in stock
  const hasInStockVariant = (product) => {
    if (!product.variants || product.variants.length === 0) return false;
    return product.variants.some(v => v.in_stock && v.stock_quantity > 0);
  };

  const filteredProducts = products.filter(product => {
    // Text search filter
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    // In stock filter
    const matchesStock = !inStockOnly || hasInStockVariant(product);
    
    return matchesSearch && matchesStock;
  });

  // Starting (lowest effective) price for a product — used for price sorting.
  const getStartingPrice = (product) => {
    const hasVariants = product.variants && product.variants.length > 0;
    if (!hasVariants) return Number(product.price) || 0;
    return Math.min(...product.variants.map(v => (v.is_on_sale && v.sale_price ? v.sale_price : v.price)));
  };

  // Sort the filtered list. Default is alphabetical A–Z so the catalog is
  // easy to browse; users can flip to Z–A or price via the Sort control.
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'price-asc':
        return getStartingPrice(a) - getStartingPrice(b);
      case 'price-desc':
        return getStartingPrice(b) - getStartingPrice(a);
      case 'name-asc':
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const handleCategoryClick = (categoryId) => {
    if (categoryId === activeCategory) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setInStockOnly(false);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Research Peptides Catalog"
        description="Browse our complete catalog of research-grade peptides. All products synthesized to ≥99% purity with full documentation and COAs available."
        keywords="peptide catalog, research peptides, buy peptides, laboratory peptides, peptide shop"
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Research Peptides
          </h1>
          <p className="text-slate-600 max-w-2xl">
            Browse our complete catalog of research-grade peptides. All products are synthesized to ≥99% purity 
            and include full documentation.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-100 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                </h3>
                {(activeCategory || searchQuery || inStockOnly) && (
                  <button 
                    onClick={clearFilters}
                    className="text-sm text-teal-700 hover:underline"
                    data-testid="clear-filters"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Search */}
              <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search peptides..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>

              {/* In Stock Filter */}
              <div className="mb-6 pb-6 border-b border-slate-100">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-10 h-6 rounded-full transition-colors ${inStockOnly ? 'bg-teal-600' : 'bg-slate-200'} relative`}>
                    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${inStockOnly ? 'translate-x-5' : 'translate-x-1'}`}></div>
                  </div>
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="sr-only"
                    data-testid="in-stock-filter"
                  />
                  <div className="flex items-center gap-2">
                    <PackageCheck className={`h-4 w-4 ${inStockOnly ? 'text-teal-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${inStockOnly ? 'text-teal-700' : 'text-slate-600'}`}>
                      In Stock Only
                    </span>
                  </div>
                </label>
              </div>

              {/* Categories */}
              <div>
                <h4 className="text-sm font-medium text-slate-900 mb-3">Categories</h4>
                <div className="space-y-2">
                  {categories.map((category) => {
                    const IconComponent = categoryIcons[category.id] || FlaskConical;
                    const isActive = activeCategory === category.id;
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategoryClick(category.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                          isActive 
                            ? 'bg-teal-50 text-teal-700 font-medium' 
                            : 'text-slate-600 hover:bg-slate-50'
                        }`}
                        data-testid={`filter-${category.id}`}
                      >
                        <IconComponent className="h-4 w-4" />
                        {category.name}
                        {isActive && <X className="h-3 w-3 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <p className="text-sm text-slate-600">
                Showing <span className="font-medium">{filteredProducts.length}</span> products
                {activeCategory && (
                  <span> in <span className="font-medium">{(categories.find(c => c.id === activeCategory)?.name) || activeCategory.replace('-', ' ')}</span></span>
                )}
              </p>
              <div className="flex items-center gap-2">
                <label htmlFor="sort-products" className="text-sm text-slate-500">Sort by</label>
                <select
                  id="sort-products"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                  data-testid="product-sort-select"
                >
                  <option value="name-asc">Name (A–Z)</option>
                  <option value="name-desc">Name (Z–A)</option>
                  <option value="price-asc">Price (Low–High)</option>
                  <option value="price-desc">Price (High–Low)</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 relative" data-testid="products-loading-skeleton" aria-busy="true" aria-live="polite">
                <span className="sr-only">Loading products…</span>
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                    <div className="aspect-square bg-slate-200 rounded-xl mb-4"></div>
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                ))}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-sm">
                    <MoleculeLoader size={72} label="Loading research peptides…" />
                  </div>
                </div>
              </div>
            ) : fetchError ? (
              <div className="text-center py-16" data-testid="products-fetch-error">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                  <FlaskConical className="h-6 w-6 text-amber-600" />
                </div>
                <h3 className="font-heading font-semibold text-slate-900 mb-2">Trouble loading products</h3>
                <p className="text-slate-500 mb-4 max-w-sm mx-auto">
                  We couldn’t reach the catalog just now — sometimes a quick retry does it. Your account is fine.
                </p>
                <Button onClick={loadProducts} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="products-retry-btn">
                  Try Again
                </Button>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <FlaskConical className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-slate-900 mb-2">No products found</h3>
                <p className="text-slate-500 mb-4">Try adjusting your search or filters</p>
                <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedProducts.map((product) => {
                  const hasVariants = product.variants && product.variants.length > 0;
                  // Compute starting price using sale price when applicable
                  const variantEffectivePrice = (v) => v.is_on_sale && v.sale_price ? v.sale_price : v.price;
                  const startingPrice = hasVariants 
                    ? Math.min(...product.variants.map(variantEffectivePrice))
                    : product.price;
                  // Find the originating variant of the starting price to show its original price
                  const startingVariant = hasVariants
                    ? product.variants.reduce((min, v) => variantEffectivePrice(v) < variantEffectivePrice(min) ? v : min, product.variants[0])
                    : null;
                  const startingOnSale = !!(startingVariant && startingVariant.is_on_sale);
                  const startingOriginal = startingOnSale ? startingVariant.original_price : null;
                  const anyOnSale = !!product.any_variant_on_sale;
                  const anyInStock = hasVariants 
                    ? product.variants.some(v => v.in_stock)
                    : product.in_stock;
                  const firstVariantSize = hasVariants ? product.variants[0].size : product.vial_size;
                  
                  return (
                    <Link key={product.product_id} to={`/products/${product.slug}`} data-testid={`product-card-${product.slug}`}>
                      <Card className="group h-full bg-white border-slate-100 overflow-hidden hover:shadow-lg transition-all duration-300">
                        <div className="aspect-square p-4 bg-slate-50 relative">
                          <img 
                            src={getThumbnailUrl(product.product_id)} 
                            alt={product.name}
                            loading="lazy"
                            decoding="async"
                            width={400}
                            height={400}
                            onError={(e) => { e.target.onerror = null; e.target.src = getImageUrl(product.image_url); }}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                          {anyOnSale && (
                            <div className="absolute top-2 left-2">
                              <Badge className="bg-rose-600 text-white shadow-sm" data-testid={`product-card-sale-${product.slug}`}>
                                Sale
                              </Badge>
                            </div>
                          )}
                          {!anyInStock && (
                            <div className="absolute top-2 right-2">
                              <Badge className="bg-red-100 text-red-700">Out of Stock</Badge>
                            </div>
                          )}
                          {user && (
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product.product_id); }}
                              className="absolute bottom-2 left-2 p-1.5 rounded-full bg-white/80 hover:bg-white shadow-sm transition-all"
                              data-testid={`wishlist-toggle-${product.slug}`}
                            >
                              <Heart className={`h-4 w-4 transition-colors ${isInWishlist(product.product_id) ? 'fill-rose-500 text-rose-500' : 'text-slate-400 hover:text-rose-400'}`} />
                            </button>
                          )}
                        </div>
                        <CardContent className="p-5">
                          <Badge className="bg-amber-50 text-amber-700 border-amber-200 mb-3 text-[10px]">
                            Research Use Only
                          </Badge>
                          <h3 className="font-heading font-semibold text-slate-900 mb-1 group-hover:text-teal-700 transition-colors">
                            {product.name}
                          </h3>
                          <p className="text-sm text-slate-500 mb-3">
                            {hasVariants 
                              ? `${product.variants.length} sizes available` 
                              : `${firstVariantSize} • ${product.purity}`
                            }
                          </p>
                          <div className="flex items-center justify-between gap-2">
                            {startingOnSale ? (
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="font-heading font-bold text-lg text-rose-600">
                                  {hasVariants ? `From $${startingPrice.toFixed(2)}` : `$${startingPrice?.toFixed(2)}`}
                                </span>
                                {startingOriginal && (
                                  <span className="text-sm text-slate-400 line-through">
                                    ${Number(startingOriginal).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="font-heading font-bold text-lg text-teal-700">
                                {hasVariants ? `From $${startingPrice.toFixed(2)}` : `$${startingPrice?.toFixed(2)}`}
                              </span>
                            )}
                            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white">
                              View Details
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
